/**
 * Central role configuration — single source of truth for role behavior.
 *
 * Adding a new role: add one entry here. No other files need changing
 * for default route and ownership bypass behavior.
 *
 * defaultRoute              — where the user lands after login
 * canBypassOwnershipCheck   — skips PropertyOwnerGuard API ownership validation
 *                             (staff roles that legitimately see all properties)
 * capabilities              — UI feature flags, wired to CapabilityGuard for route protection
 *                             extend here when needed, not inline in components
 */

/**
 * Role groups — mirrors be/config/roles.js for frontend use.
 * Import these instead of hardcoding role arrays in components.
 */
export const CORE_STAFF_ROLES = ['admin', 'ops', 'ops-manager'];
/** Mirrors OWNER_ROLES in be/config/roles.js — the one property-owner role. The retired
 *  'diyOwner' / 'propertyOwner' / 'owner' names are gone (tasks/owner-screens-mobile.md D5). */
export const OWNER_ROLES = ['turnKeyOwner'];
export const isOwnerRole = (role) => OWNER_ROLES.includes(role);
export const TASK_MANAGER_ROLES = ['admin', 'ops-manager', 'maintenance-supervisor'];
export const SERVICE_STAFF_ROLES = ['admin', 'ops', 'ops-manager', 'maintenance-supervisor'];
/** Mirrors FINANCE_ROLES in be/config/roles.js — the roles `isFinanceViewer` accepts. */
export const FINANCE_ROLES = ['admin', 'ops', 'ops-manager', 'maintenance-supervisor'];
/** Mirrors TASK_WORKER_ROLES in be/config/roles.js — who can take (be assigned) a task: ops + the crew. */
export const TASK_WORKER_ROLES = ['admin', 'ops', 'ops-manager', 'maintenance', 'maintenance-supervisor'];
/** Mirrors FIELD_CREW_ROLES in be/config/roles.js — who physically cleans, so who may tap «انضممت». */
export const FIELD_CREW_ROLES = ['maintenance', 'maintenance-supervisor'];
/**
 * Everyone a registered push-notification type can address — mirrors `ALL_AUDIENCE_ROLES` in
 * `be/services/notifications/types.js`, which DERIVES it from the type registry rather than
 * declaring it. Widened beyond core staff on 2026-08-04 by the `guest_checkout` type
 * (tasks/manual-guest-checkout-event.md D31/D34), which is addressed to the cleaning crew.
 *
 * ⚠️ This decides only whether the settings PANEL renders. Which toggles appear inside it is the
 * server's answer, per type, per role — never a list here. The backend is the enforcement point and
 * 403s anyone outside this group regardless.
 */
export const NOTIFICATION_AUDIENCE_ROLES = [
  'admin', 'ops', 'ops-manager', 'maintenance', 'maintenance-supervisor',
  // Owners since 2026-10-05: new-booking + cancellation pushes (tasks/owner-screens-mobile.md step 6)
  'turnKeyOwner'
];
/**
 * Mirrors SUPERVISOR_ROLES in be/config/roles.js — may override another person's operational state
 * (today: conversation force-release, D81). The server is the enforcement point; this only decides
 * whether to render an action that would otherwise be refused.
 */
export const SUPERVISOR_ROLES = ['admin', 'ops-manager'];
/**
 * Mirrors BILLING_READ_ROLES / BILLING_MANAGE_ROLES in be/config/roles.js — the owner
 * commission billing worksheet.
 *
 * ⚠️ Deliberately NOT FINANCE_ROLES: that group includes `maintenance-supervisor`, who
 * has operational finance visibility but must not see what Host Me bills an owner.
 * `accountant` is an external contractor whose entire application is this one page.
 * The server is the enforcement point; these only decide what to render.
 */
export const BILLING_READ_ROLES = ['admin', 'ops', 'ops-manager', 'accountant'];
/**
 * Pricing Guidance (/pricing-assistant). Mirrors `authorize('admin', 'ops')` on every route in
 * be/routes/pricingRoutes.js (no backend constant). The server is the enforcement point; this
 * decides the page's guard and whether «التقويم» offers a link to it.
 */
export const PRICING_ROLES = ['admin', 'ops'];
export const BILLING_MANAGE_ROLES = ['admin', 'ops', 'ops-manager'];
/**
 * Who sees a billing row's booking reference as a LINK to the booking details page.
 * The accountant is excluded on purpose: her application is the billing page alone, so a
 * link would only lead her somewhere she cannot use (tasks/billing-row-link-to-booking.md).
 */
export const BILLING_BOOKING_LINK_ROLES = ['admin', 'ops', 'ops-manager'];

export const roleConfig = {
  admin: {
    defaultRoute: '/admin/dashboard',
    canBypassOwnershipCheck: true,
    capabilities: {
      viewProperties: true,
      editProperties: true,
      viewTasks: true,
      editTasks: true,
      viewGuests: true,
      manageGuests: true,
      manageServices: true,
    }
  },

  ops: {
    defaultRoute: '/properties',
    canBypassOwnershipCheck: true,
    capabilities: {
      viewProperties: true,
      editProperties: false,
      viewTasks: true,
      editTasks: true,
      viewGuests: true,
      manageGuests: true,
      manageServices: true,
    }
  },

  maintenance: {
    defaultRoute: '/my-tasks',
    canBypassOwnershipCheck: true,
    capabilities: {
      viewProperties: true,
      editProperties: false,
      viewTasks: true,
      editTasks: false,
      viewGuests: false,
      manageGuests: false,
      manageServices: false,
    }
  },

  turnKeyOwner: {
    defaultRoute: '/dashboard',
    canBypassOwnershipCheck: false,
    capabilities: {
      viewProperties: true,
      editProperties: false,
      viewTasks: false,
      editTasks: false,
      viewGuests: true,
      manageGuests: false,
      manageServices: false,
    }
  },

  serviceClient: {
    defaultRoute: '/client/t/:token',
    canBypassOwnershipCheck: false,
    capabilities: {
      viewProperties: false,
      editProperties: false,
      viewTasks: false,
      editTasks: false,
      viewGuests: false,
      manageGuests: false,
      manageServices: false,
    }
  },

  'maintenance-supervisor': {
    // The «المتابعة» tab on /tasks. The standalone supervisor dashboard it replaced was retired on
    // 2026-09-20 (tasks/supervisor-view.md D1/D3) — two task pages was the duplication.
    defaultRoute: '/tasks?view=attention',
    canBypassOwnershipCheck: true,
    capabilities: {
      viewProperties: true,
      editProperties: false,
      viewTasks: true,
      editTasks: true,
      viewGuests: true,
      manageGuests: true,
      viewFinance: true,
      manageServices: true,
    }
  },

  'ops-manager': {
    defaultRoute: '/properties',
    canBypassOwnershipCheck: true,
    capabilities: {
      viewProperties: true,
      editProperties: false,
      viewTasks: true,
      editTasks: true,
      viewGuests: true,
      manageGuests: true,
      manageServices: true,
    }
  },

  /**
   * External accountant — read-only, one page (the owner billing worksheet).
   *
   * ⚠️ `defaultRoute` MUST stay a route this role can actually load. An unknown or
   * unreachable one falls through RoleGuard → `/admin/dashboard` (which has no
   * fallbackPath) → `/login` → back to the default route: an unbounded redirect cycle
   * with no reachable logout.
   *
   * Every capability is false: this role reaches its one page through an explicit
   * RoleGuard, never through a capability.
   */
  accountant: {
    defaultRoute: '/billing',
    canBypassOwnershipCheck: false,
    capabilities: {
      viewProperties: false,
      editProperties: false,
      viewTasks: false,
      editTasks: false,
      viewGuests: false,
      manageGuests: false,
      manageServices: false,
    }
  },

  service: {
    defaultRoute: '/properties',
    canBypassOwnershipCheck: true,
    capabilities: {
      viewProperties: true,
      editProperties: false,
      viewTasks: false,
      editTasks: false,
      viewGuests: false,
      manageGuests: false,
      manageServices: false,
    }
  }
};
